import { useState, useCallback } from 'react';
import { FileBarChart, Printer } from 'lucide-react';
import { taxApi } from '@/api/tax.api';
import type { TaxSummaryReportItem } from '@/api/tax.api';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

const fmtAmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2 });

export const TaxSummaryReportPage = () => {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

  const [dateFrom,    setDateFrom]    = useState(firstOfMonth);
  const [dateTo,      setDateTo]      = useState(today);
  const [taxCodeId] = useState('');
  const [inputFilter, setInputFilter] = useState<'all' | 'input' | 'output'>('all');

  const [items,   setItems]   = useState<TaxSummaryReportItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [ran,     setRan]     = useState(false);

  const run = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params: Record<string, unknown> = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo)   params.dateTo   = dateTo;
      if (taxCodeId) params.taxCodeId = taxCodeId;
      if (inputFilter === 'input')  params.isInputTax = true;
      if (inputFilter === 'output') params.isInputTax = false;
      const res = await taxApi.getTaxSummaryReport(params);
      setItems(res.data?.data?.lines ?? []);
      setRan(true);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, taxCodeId, inputFilter]);

  const handleExportCSV = () => {
    if (!items.length) return;
    const header = ['Tax Code', 'Tax Name', 'Jurisdiction', 'Taxable Amount', 'Tax Amount', 'Recoverable', 'Non-Recoverable', 'Count'];
    const rows = items.map(r => [
      r.taxCode, r.taxName, r.jurisdictionName,
      r.totalTaxableAmount.toFixed(2), r.totalTaxAmount.toFixed(2),
      r.totalRecoverableAmount.toFixed(2), r.totalNonRecoverableAmount.toFixed(2),
      r.transactionCount,
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tax-summary.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Totals
  const totals = items.reduce((acc, r) => ({
    taxable:    acc.taxable    + r.totalTaxableAmount,
    tax:        acc.tax        + r.totalTaxAmount,
    recoverable: acc.recoverable + r.totalRecoverableAmount,
    nonRecov:   acc.nonRecov  + r.totalNonRecoverableAmount,
    count:      acc.count     + r.transactionCount,
  }), { taxable: 0, tax: 0, recoverable: 0, nonRecov: 0, count: 0 });

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
          <FileBarChart size={20} className="text-brand-600" /> Tax Summary Report
        </h1>

        {/* Filter panel */}
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Date From *</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Date To *</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-brand-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">Input / Output</label>
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
              {(['all', 'input', 'output'] as const).map(v => (
                <button key={v} onClick={() => setInputFilter(v)}
                  className={cn('px-3 py-1.5 text-xs font-medium transition-colors',
                    inputFilter === v ? 'bg-brand-600 text-white' : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60')}>
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <button onClick={run} disabled={loading || !dateFrom || !dateTo}
            className="h-9 px-5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 flex items-center gap-1.5 transition-colors">
            {loading && <Spinner size="sm" />} Run Report
          </button>
          {ran && !loading && (
            <>
              <button onClick={() => window.print()}
                className="h-9 px-4 flex items-center gap-1.5 text-sm border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                <Printer size={14} /> Print
              </button>
              <button onClick={handleExportCSV}
                className="h-9 px-4 flex items-center gap-1.5 text-sm border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                Export CSV
              </button>
            </>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-auto">
        {loading && (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        )}
        {!loading && ran && items.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <FileBarChart size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No data for the selected filters</p>
          </div>
        )}
        {!loading && !ran && (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <FileBarChart size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">Set date range and click Run Report</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                {['Tax Code', 'Tax Name', 'Jurisdiction', 'Taxable Amount', 'Tax Amount', 'Recoverable', 'Non-Recoverable', 'Count'].map(h => (
                  <th key={h} className={cn('px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap',
                    ['Taxable Amount', 'Tax Amount', 'Recoverable', 'Non-Recoverable', 'Count'].includes(h) ? 'text-right' : 'text-left')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {items.map((row, idx) => (
                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono font-semibold text-brand-700 dark:text-brand-400">{row.taxCode}</td>
                  <td className="px-4 py-2.5 text-slate-800 dark:text-slate-200">{row.taxName}</td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-400">{row.jurisdictionName}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-800 dark:text-slate-200">{fmtAmt(row.totalTaxableAmount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-semibold text-blue-700 dark:text-blue-400">{fmtAmt(row.totalTaxAmount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-green-700 dark:text-green-400">{fmtAmt(row.totalRecoverableAmount)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-red-600 dark:text-red-400">{fmtAmt(row.totalNonRecoverableAmount)}</td>
                  <td className="px-4 py-2.5 text-right text-slate-600 dark:text-slate-400">{row.transactionCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
            {/* Totals row */}
            <tfoot>
              <tr className="bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-300 dark:border-slate-600">
                <td colSpan={3} className="px-4 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Totals</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-800 dark:text-slate-200">{fmtAmt(totals.taxable)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold text-blue-700 dark:text-blue-400">{fmtAmt(totals.tax)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold text-green-700 dark:text-green-400">{fmtAmt(totals.recoverable)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold text-red-600 dark:text-red-400">{fmtAmt(totals.nonRecov)}</td>
                <td className="px-4 py-2.5 text-right font-mono font-bold text-slate-700 dark:text-slate-300">{totals.count.toLocaleString()}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};
